import React, { useState, useEffect } from 'react';
import axios from 'axios';

// DNS Instructions Component
const DNSInstructions = ({ domain, dnsConfig }) => {
  if (!dnsConfig || (!dnsConfig.serverIP && !dnsConfig.cnameTarget)) {
    return (
      <div className="bg-gray-50 p-3 rounded text-sm">
        <p className="text-gray-600">DNS configuration not available. Contact support for setup instructions.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg text-sm space-y-4">
      <p className="font-medium text-gray-800">Configure your domain DNS:</p>

      {dnsConfig.serverIP && (
        <div className="bg-white p-3 rounded border border-gray-200">
          <p className="font-semibold text-blue-700 mb-2">Option 1: A Record (Recommended)</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-gray-500">Type:</div>
            <div className="font-mono">A</div>
            <div className="text-gray-500">Name/Host:</div>
            <div className="font-mono">@ (or {domain})</div>
            <div className="text-gray-500">Value/Points to:</div>
            <div className="font-mono text-green-700">{dnsConfig.serverIP}</div>
            <div className="text-gray-500">TTL:</div>
            <div className="font-mono">3600 (1 hour)</div>
          </div>
        </div>
      )}

      {dnsConfig.cnameTarget && (
        <div className="bg-white p-3 rounded border border-gray-200">
          <p className="font-semibold text-blue-700 mb-2">Option 2: CNAME Record</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="text-gray-500">Type:</div>
            <div className="font-mono">CNAME</div>
            <div className="text-gray-500">Name/Host:</div>
            <div className="font-mono">www</div>
            <div className="text-gray-500">Value/Points to:</div>
            <div className="font-mono text-green-700">{dnsConfig.cnameTarget}</div>
            <div className="text-gray-500">TTL:</div>
            <div className="font-mono">3600 (1 hour)</div>
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 italic">
        DNS changes may take up to 48 hours to propagate globally. Click "Verify DNS" after configuration.
      </p>
    </div>
  );
};

// Verification Result Component
const VerificationResult = ({ result }) => {
  if (!result) return null;

  if (result.verified) {
    return (
      <div className="mt-3 bg-green-50 border border-green-200 p-3 rounded text-sm">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <p className="font-medium text-green-800">DNS Verified Successfully!</p>
        </div>
        <p className="text-green-700 mt-1">
          {result.type} record pointing to <span className="font-mono">{result.value}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 bg-yellow-50 border border-yellow-200 p-3 rounded text-sm">
      <div className="flex items-center gap-2">
        <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="font-medium text-yellow-800">DNS Not Configured</p>
      </div>
      <p className="text-yellow-700 mt-1">{result.error || 'Domain not pointing to our servers yet.'}</p>
      {result.details?.aRecord?.found && (
        <p className="text-yellow-600 mt-1 text-xs">
          Found A records: {result.details.aRecord.found.join(', ')}
        </p>
      )}
    </div>
  );
};

const DomainManager = ({ siteId, siteName, onClose }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dnsConfig, setDnsConfig] = useState(null);
  const [verificationResults, setVerificationResults] = useState({});
  const [checkingDomain, setCheckingDomain] = useState(null);
  const [forceActivating, setForceActivating] = useState(null);
  const [isDev, setIsDev] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);

  useEffect(() => {
    fetchDomains();
    fetchDNSConfig();
    fetchDevStatus();
  }, [siteId]);

  const fetchDomains = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/domains/site/${siteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data);
    } catch (error) {
      console.error('Error fetching domains:', error);
      setError('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchDNSConfig = async () => {
    try {
      const response = await axios.get('/api/caddy/dns-config');
      setDnsConfig(response.data);
    } catch (error) {
      console.error('Error fetching DNS config:', error);
      // Non-critical, continue without DNS config
    }
  };

  const fetchDevStatus = async () => {
    try {
      const response = await axios.get('/api/caddy/status');
      setIsDev(response.data.isDev || false);
    } catch (error) {
      console.error('Error fetching dev status:', error);
      // Non-critical, default to false (production mode)
    }
  };

  const addDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    setAdding(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/domains', {
        domain: newDomain.trim(),
        siteId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDomains([response.data, ...domains]);
      setNewDomain('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const deleteDomain = (domainId, domainName) => {
    setDeleteConfirmation({ domainId, domainName });
  };

  const confirmDeleteDomain = async () => {
    if (!deleteConfirmation) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/domains/${deleteConfirmation.domainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setDomains(domains.filter(d => d._id !== deleteConfirmation.domainId));
      setDeleteConfirmation(null);
    } catch (error) {
      setError('Failed to delete domain');
      setDeleteConfirmation(null);
    }
  };

  const checkDomain = async (domainId) => {
    setCheckingDomain(domainId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/domains/${domainId}/check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update domain in the list
      setDomains(domains.map(d =>
        d._id === domainId ? response.data.domain : d
      ));

      // Store verification result
      if (response.data.verification) {
        setVerificationResults(prev => ({
          ...prev,
          [domainId]: response.data.verification
        }));
      }

      // Update DNS config if returned
      if (response.data.dnsConfig) {
        setDnsConfig(response.data.dnsConfig);
      }
    } catch (error) {
      setError('Failed to check domain configuration');
    } finally {
      setCheckingDomain(null);
    }
  };

  const forceActivate = async (domainId) => {
    setForceActivating(domainId);
    setError('');
    setSuccessMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/domains/${domainId}/force-activate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update domain in the list
      setDomains(domains.map(d =>
        d._id === domainId ? response.data.domain : d
      ));

      setSuccessMessage(response.data.message);

      // Clear verification results for this domain
      setVerificationResults(prev => {
        const newResults = { ...prev };
        delete newResults[domainId];
        return newResults;
      });
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to force activate domain');
    } finally {
      setForceActivating(null);
    }
  };

  const requestSSL = async (domainId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/domains/${domainId}/ssl/request`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh domains to show updated SSL status
      fetchDomains();
    } catch (error) {
      setError('Failed to request SSL certificate');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getSSLStatusColor = (sslStatus) => {
    switch (sslStatus) {
      case 'issued': return 'text-green-600';
      case 'pending': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4">Loading domains...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Domain Management</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Site: {siteName}</h3>
          <p className="text-gray-600 text-sm">
            Manage custom domains for your site. Point your domain to our servers to make your site accessible via your custom domain.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Add Domain Form */}
        <form onSubmit={addDomain} className="mb-6">
          <div className="flex gap-3">
            <div className="flex-1">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={adding}
              />
            </div>
            <button
              type="submit"
              disabled={adding || !newDomain.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {adding ? 'Adding...' : 'Add Domain'}
            </button>
          </div>
        </form>

        {/* Domains List */}
        <div className="space-y-4">
          {domains.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No custom domains configured. Add your first domain above.
            </div>
          ) : (
            domains.map((domain) => (
              <div key={domain._id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-semibold">{domain.domain}</h4>
                    <div className="flex items-center gap-4 mt-1">
                      <span className={`text-sm flex items-center gap-1 ${getStatusColor(domain.status)}`}>
                        {domain.status === 'active' && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                        Status: {domain.status}
                      </span>
                      <span className={`text-sm ${getSSLStatusColor(domain.sslStatus)}`}>
                        SSL: {domain.sslStatus === 'none' ? 'Auto (Caddy)' : domain.sslStatus}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => checkDomain(domain._id)}
                      disabled={checkingDomain === domain._id}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50 flex items-center gap-1"
                    >
                      {checkingDomain === domain._id && (
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                      )}
                      {checkingDomain === domain._id ? 'Checking...' : 'Verify DNS'}
                    </button>
                    {isDev && domain.status !== 'active' && (
                      <button
                        onClick={() => forceActivate(domain._id)}
                        disabled={forceActivating === domain._id}
                        className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded hover:bg-orange-200 disabled:opacity-50 flex items-center gap-1 border border-orange-300"
                        title="Skip DNS verification (development only)"
                      >
                        {forceActivating === domain._id ? (
                          <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        ) : (
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                          </svg>
                        )}
                        {forceActivating === domain._id ? 'Activating...' : 'DEV: Force Activate'}
                      </button>
                    )}
                    <button
                      onClick={() => deleteDomain(domain._id, domain.domain)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {/* DNS Instructions - use new component if config available */}
                {dnsConfig && (dnsConfig.serverIP || dnsConfig.cnameTarget) ? (
                  <DNSInstructions domain={domain.domain} dnsConfig={dnsConfig} />
                ) : (
                  <div className="bg-gray-50 p-3 rounded text-sm">
                    <p className="font-medium mb-2">DNS Configuration:</p>
                    <pre className="whitespace-pre-wrap text-xs text-gray-700">
                      {domain.dnsInstructions}
                    </pre>
                  </div>
                )}

                {/* Verification Result */}
                <VerificationResult result={verificationResults[domain._id]} />

                {/* SSL Certificate Info */}
                {domain.sslCertificate && domain.sslCertificate.issuer && (
                  <div className="mt-3 bg-green-50 p-3 rounded text-sm">
                    <p className="font-medium text-green-800">SSL Certificate:</p>
                    <p className="text-green-700">
                      Issued by: {domain.sslCertificate.issuer}
                    </p>
                    <p className="text-green-700">
                      Expires: {new Date(domain.sslCertificate.expiresAt).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Note about Caddy auto-SSL */}
                {domain.status === 'active' && (!domain.sslCertificate || !domain.sslCertificate.issuer) && (
                  <div className="mt-3 bg-blue-50 p-3 rounded text-sm">
                    <p className="font-medium text-blue-800">SSL Information:</p>
                    <p className="text-blue-700">
                      SSL certificates are automatically provisioned by Caddy when your domain is accessed via HTTPS.
                    </p>
                  </div>
                )}

                {domain.errorMessage && (
                  <div className="mt-3 bg-red-50 p-3 rounded text-sm">
                    <p className="font-medium text-red-800">Error:</p>
                    <p className="text-red-700">{domain.errorMessage}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Domain
              </h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete <span className="font-medium">{deleteConfirmation.domainName}</span>? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmation(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteDomain}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainManager;